import React, { useEffect, useState, useMemo } from "react";
import PrimaryButton from "../../ui-library/Buttons/PrimaryButton/PrimaryButton";
import SecondaryButton from "../../ui-library/Buttons/SecondaryButton/SecondaryButton";
import CustomDropdown from "../../ui-library/CustomDropdown/CustomDropdown";
import styled, { withTheme } from "styled-components";
import SetDailyGoals from "../SetDailyGoals/SetDailyGoals";
import LabelledInput from "../../ui-library/LabelledInput/LabelledInput";
import { getStudyStatusAvailableOptions } from "../../../modules/constants";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const FieldTitle = styled.span`
  color: ${(props) => props.theme?.nero};
  font-weight: 600;
  font-size: 16px;
  margin-right: 14px;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 38px;
  @media (max-width: 576px) {
    padding-top: 2rem;
    margin-top: auto;
    .btn {
      width: 100%;
    }
  }
`;

const SetDailyGoalsContainer = styled.div`
  display: flex;
  margin-top: 27px;
`;

const StyledLabelledInput = styled(LabelledInput)`
  margin-top: 27px;
`;

const ChangeStudyStatus = (props) => {
  const { currentStatus, onSubmit, onCancel, processing, currentDailyTargets } =
    props;

  const [selectedStatus, setSelectedStatus] = useState(null);
  const [dailyTargets, setDailyTargets] = useState(null);
  const [updateReason, setUpdateReason] = useState("");

  const [updateReasonIsValid, setUpdateReasonIsValid] = useState(false);
  const [formValid, setFormValid] = useState(false);

  const [reasonForChangeError, setReasonForChangeError] = useState(null);

  const studyStatusAvailableOptions = useMemo(() => {
    if (!currentStatus) {
      return null;
    }

    return getStudyStatusAvailableOptions(currentStatus);
  }, [currentStatus]);

  useEffect(() => {
    setUpdateReasonIsValid(
      updateReason.replace(/\.|,/g, "").trim() !== "" &&
        updateReason.length <= 450
    );
  }, [updateReason]);

  const validateReason = (reason) => {
    setReasonForChangeError(null);
    if (reason.replace(/\.|,/g, "").trim() === "") {
      setReasonForChangeError("Please provide a reason");
    }
    if (reason.length > 450) {
      setReasonForChangeError(
        "Please enter a reason with less than 450 characters"
      );
    }
  };

  const onChangeDailyTargets = (valid, goals) => {
    setFormValid(valid);
    setDailyTargets(valid ? goals : null);
  };

  const onSave = () => {
    onSubmit(selectedStatus, dailyTargets, updateReason);
  };

  return (
    <Container data-testid="change-study-status">
      {currentStatus && (
        <Row>
          <FieldTitle>Current Status</FieldTitle>
          <CustomDropdown
            data-testid="current-status-dropdown"
            value={currentStatus.value}
            disabled
            width={190}
          />
        </Row>
      )}

      {studyStatusAvailableOptions && (
        <Row className="mt-4">
          <FieldTitle>Update to</FieldTitle>
          <CustomDropdown
            data-testid="new-status-dropdown"
            placeholder="Select"
            value={selectedStatus?.value}
            options={Object.values(studyStatusAvailableOptions)}
            width={190}
            onChange={(value) => setSelectedStatus(value)}
          />
        </Row>
      )}

      {selectedStatus?.showTargets && (
        <SetDailyGoalsContainer data-testid="setdailygoals-container">
          <SetDailyGoals
            onChange={onChangeDailyTargets}
            defaultTargets={currentDailyTargets}
          />
        </SetDailyGoalsContainer>
      )}

      <StyledLabelledInput
        title="Reason for a status change"
        type="text"
        data-testid="update-reason-input"
        value={updateReason}
        onChange={(e) => setUpdateReason(e.target.value)}
        onBlur={(e) => validateReason(e.target.value)}
        hasError={!!reasonForChangeError}
        errorMessage={reasonForChangeError}
      />

      {onSubmit && (
        <ActionButtonsContainer>
          {onCancel && (
            <SecondaryButton
              data-testid="cancel-button"
              className="me-2"
              onClick={onCancel}
            >
              Cancel
            </SecondaryButton>
          )}
          <PrimaryButton
            data-testid="submit-button"
            disabled={
              !selectedStatus ||
              !updateReasonIsValid ||
              processing ||
              (selectedStatus.showTargets && !formValid)
            }
            onClick={onSave}
          >
            Save
          </PrimaryButton>
        </ActionButtonsContainer>
      )}
    </Container>
  );
};

ChangeStudyStatus.defaultProps = {
  onSubmit: null,
  currentStatus: null,
  hasError: false,
};

export default withTheme(ChangeStudyStatus);
